import React, { useState } from 'react';
import { 
  Eye, Users, TrendingUp, Mail, Calendar, Download, RefreshCw,
  ArrowRight, BarChart2
} from 'lucide-react';
import { GroupedBarChart } from '../components/charts/GroupedBarChart';
import { DetailedBarChart } from '../components/charts/DetailedBarChart';
import { LineChart } from '../components/charts/LineChart';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useDashboardMetrics } from '../hooks/useDashboardMetrics';
import { useAcmBase } from '../hooks/useAcmBase';
import { useNavigate } from 'react-router-dom';
import { RecentActivity } from '../components/RecentActivity';
import { formatNumber } from '../utils/formatters';

type TimeScale = 'day' | 'month' | 'year';

export default function Dashboard() {
  const navigate = useNavigate();
  const { metrics, loading, error, refresh } = useDashboardMetrics();
  const { 
    totalCount, 
    activeCount, 
    loyalCount, 
    newStudentsCount,
    growthPercentage,
    activeGrowthPercentage
  } = useAcmBase();
  const [timeScale, setTimeScale] = useState<TimeScale>('month');

  // Formata os dados para o gráfico
  const engagementData = React.useMemo(() => {
    if (!metrics.engagementData || metrics.engagementData.length === 0) return [];

    return metrics.engagementData.map(item => ({
      date: new Date(item.created_at),
      clicked_links_total: item.clicked_links_total || 0,
      opened_emails_total: item.opened_emails_total || 0
    }));
  }, [metrics.engagementData]);

  // Dados para o gráfico de linha
  const lineChartData = React.useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      name: `${i + 1}`,
      value: Math.floor(Math.random() * 100)
    }));
  }, []);

  return (
    <div className="p-8">
      <div className="max-w-[1200px] mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Dashboard</h1>
            <p className="text-gray-400 text-sm">
              {new Date().toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
              })}
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={refresh}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Exportar dados
            </Button>
            <Button onClick={() => navigate('/students')}>
              Lista de Alunos
            </Button>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle>
                <div className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-[#268c73]" />
                  <span>E-mails Abertos</span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-1">
                <span className="text-2xl font-bold">
                  {loading ? "..." : formatNumber(metrics.totalOpens)}
                </span>
                <span className="text-sm text-muted-foreground">
                  +15.2% vs. mês anterior
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#268c73]" />
                  <span>E-mails Relevantes</span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-1">
                <span className="text-2xl font-bold">
                  {loading ? "..." : formatNumber(metrics.totalUserEmails)}
                </span>
                <span className="text-sm text-muted-foreground">
                  +8.7% vs. mês anterior
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#268c73]" />
                  <span>Taxa de Clique</span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-1">
                <span className="text-2xl font-bold">
                  {loading ? "..." : `${metrics.clickThroughRate.toFixed(1)}%`}
                </span>
                <span className="text-sm text-muted-foreground">
                  +3.1% vs. mês anterior
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[#268c73]" />
                  <span>E-mails Enviados</span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-1">
                <span className="text-2xl font-bold">
                  {loading ? "..." : formatNumber(metrics.sentEmails)}
                </span>
                <span className="text-sm text-muted-foreground">
                  +11.3% vs. mês anterior
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {/* Engagement Chart */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>Engajamento por Período</CardTitle>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="text-2xl font-bold">
                      {loading ? "..." : `${metrics.linkVsEmails.toFixed(1)}%`}
                    </span>
                    <span className="text-green-400 text-sm">+24.5%</span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-[#fb923c]"></div>
                      <span className="text-sm text-muted-foreground">Cliques</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-400"></div>
                      <span className="text-sm text-muted-foreground">Aberturas</span>
                    </div>
                  </div>
                  <select 
                    value={timeScale}
                    onChange={(e) => setTimeScale(e.target.value as TimeScale)}
                    className="bg-[#2C2D3C] text-sm text-gray-400 rounded-lg px-3 py-1.5 border border-gray-700"
                  >
                    <option value="day">Por Dia</option>
                    <option value="month">Por Mês</option>
                    <option value="year">Por Ano</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <GroupedBarChart 
                  data={engagementData}
                  loading={loading}
                  timeScale={timeScale}
                />
              </div>
            </CardContent>
          </Card>

          {/* Secondary Charts Grid */}
          <div className="grid grid-cols-3 gap-6">
            {/* Open Rate */}
            <DetailedBarChart 
              openRate={metrics.openRate}
              totalEmails={metrics.sentEmails}
              openedEmails={metrics.totalOpens}
              growth={metrics.emailOpensTrend}
              loading={loading}
            />

            {/* Interactions */}
            <LineChart 
              data={lineChartData}
              title="Interações"
              loading={loading}
            />

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Atividade Recente</CardTitle>
                  <Button
                    variant="link"
                    onClick={() => navigate('/students')}
                    className="text-[#268c73] hover:text-[#1a7a61]"
                  >
                    Ver todos
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <RecentActivity />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}